import { Auth0AccountRole, CRMVisualizationResponse } from "client/openapi";
import type { BoardBadges } from "client/openapi/models/BoardBadges";
import { PersonKind } from "client/openapi/models/PersonKind";
import { PeopleService } from "client/openapi/services/PeopleService";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import MessageBubble from "components/Dashboard/OrgCommunication/MessageBubble";
import ReplyBox from "components/Dashboard/OrgCommunication/ReplyBox";
import {
  newestInbound,
  rowChannel,
} from "components/Dashboard/OrgCommunication/conversations";
import MeetingRequests from "components/Dashboard/PeoplePanel/MeetingRequests";
import OnboardingSteps from "components/Dashboard/PeoplePanel/OnboardingSteps";
import { IssueChips } from "components/Dashboard/PeoplePanel/PersonTableRow";
import TaskViewDialog from "components/Dashboard/PeoplePanel/TaskViewDialog";
import {
  dismissIssues,
  personKey,
  personName,
  profilePath,
} from "components/Dashboard/PeoplePanel/person";
import { Dialog, DialogContent } from "components/Dialog";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IconType } from "react-icons";
import { GoAlert, GoChecklist, GoComment, GoRocket } from "react-icons/go";
import { Link } from "react-router-dom";
import Notifications from "util/notifications";
import { useUnread } from "util/UnreadContext";

type Chip = "messages" | "tasks" | "needs_attention" | "onboarding";

/**
 * The chips: unread messages first (the most time-sensitive), in blue, then
 * the People panel's colors: tasks, alerts, onboarding steps.
 */
const CHIPS: {
  chip: Chip;
  label: string;
  Icon: IconType;
  className: string;
}[] = [
  {
    chip: "messages",
    label: "Messages",
    Icon: GoComment,
    className: "people-chip board-chip--messages",
  },
  {
    chip: "tasks",
    label: "Tasks",
    Icon: GoChecklist,
    className: "people-chip people-chip--task",
  },
  {
    chip: "needs_attention",
    label: "Needs attention",
    Icon: GoAlert,
    className: "people-chip people-chip--alert",
  },
  {
    chip: "onboarding",
    label: "Onboarding",
    Icon: GoRocket,
    className: "people-step",
  },
];

/** A card's person kind, by the board column's type. */
export const CARD_KINDS: Record<string, PersonKind> = {
  [Auth0AccountRole.ME]: PersonKind.STUDENT,
  [Auth0AccountRole.PARENT]: PersonKind.PARENT,
  [Auth0AccountRole.ORG_TUTOR]: PersonKind.TUTOR,
};

/** Everyone on the board as one sorted string: the same people, the same key. */
function boardPeople(board?: CRMVisualizationResponse): string {
  const keys = new Set<string>();
  board?.columns?.forEach((column) => {
    column.students_in_column?.forEach(({ id }) =>
      keys.add(personKey({ kind: PersonKind.STUDENT, id }))
    );
    column.parents_in_column?.forEach(({ id }) =>
      keys.add(personKey({ kind: PersonKind.PARENT, id }))
    );
    column.tutors_in_column?.forEach(({ id }) =>
      keys.add(personKey({ kind: PersonKind.TUTOR, id }))
    );
  });
  return Array.from(keys).sort().join(",");
}

// A press on a chip never starts a drag of its card.
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * A chip's window: the tasks in the People panel's task window; Needs
 * attention and Onboarding with the People panel's chips and steps, and
 * Dismiss; Messages with each conversation's unread messages and reply box
 * as the Communication view shows them, and Mark read. A reply marks its
 * conversation read, as in the Communication view; opening the window marks
 * nothing read.
 */
function ChipWindow({
  badge,
  chip,
  orgId,
  organizationName,
  onClose,
  onChange,
  onDismiss,
}: {
  badge: BoardBadges;
  chip: Chip;
  orgId: number;
  /** A received message's first recipient on the From and To line. */
  organizationName: string;
  onClose: () => void;
  onChange: () => void;
  /** Needs attention's or Onboarding's Dismiss. */
  onDismiss: () => void;
}) {
  const { row, conversations } = badge;
  const [saving, setSaving] = useState(false);
  const { setRead } = useUnread();

  if (chip === "tasks") {
    return (
      <TaskViewDialog
        tasks={row.tasks ?? []}
        orgId={orgId}
        onClose={onClose}
        onChanged={onChange}
      />
    );
  }

  // Every unread message of these rows, through the one read state, so the
  // dashboard's Communication count updates too.
  function markRead(rows: string[]) {
    setSaving(true);
    setRead({ rows }, true)
      .then(onChange)
      .catch(() => Notifications.error("That could not be saved. Try again."))
      .finally(() => setSaving(false));
  }

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="dialog-content max-w-[560px]">
        <h2 className="text-lg font-bold">
          {CHIPS.find((c) => c.chip === chip)?.label}
        </h2>
        <Link
          to={profilePath(row)}
          target="_blank"
          className="text-sm text-gray-600 hover:underline"
        >
          {personName(row) || "No name"}
        </Link>
        {chip === "messages" ? (
          conversations.map((conversation) => (
            <div key={conversation.row} className="mt-4 flex flex-col gap-2">
              {conversation.messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  organizationName={organizationName}
                />
              ))}
              <ReplyBox
                row={conversation.row}
                messages={conversation.messages}
                orgId={orgId}
                answered={
                  rowChannel(conversation.messages) === "email"
                    ? newestInbound(conversation.messages)
                    : undefined
                }
                onSent={() => markRead([conversation.row])}
              />
            </div>
          ))
        ) : (
          <div className="mt-4 flex flex-wrap gap-1">
            {chip === "onboarding" ? (
              <OnboardingSteps row={row} orgId={orgId} onChange={onChange} />
            ) : (
              <>
                {row.requests && row.requests.length > 0 && (
                  <MeetingRequests
                    requests={row.requests}
                    onAnswered={onChange}
                  />
                )}
                <IssueChips row={row} orgId={orgId} onChange={onChange} />
              </>
            )}
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <Button
            size={ButtonSize.SMALL}
            fill={ButtonFill.HOLLOW}
            color={ButtonColor.GREEN}
            disabled={saving}
            onClick={
              chip === "messages"
                ? () => markRead(conversations.map((c) => c.row))
                : onDismiss
            }
          >
            {chip === "messages" ? "Mark read" : "Dismiss"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The CRM board's card chips: Messages, Tasks, Needs attention and
 * Onboarding, each with its count, shown when at least 1. One request loads
 * every card's; it runs again when the board's people change, after an
 * action in a chip's window, and when the minute's unread poll finds
 * something new, so the Messages chips and window keep up with new messages
 * the way the Communication view does (Mark read marks whole rows). `card`
 * renders a card's chips; `chipWindow` the open chip's window (outside the
 * cards, so nothing in it reaches a card); `reload` loads the counts again
 * (a task created from a card).
 */
export default function useBoardChips(
  orgId: number,
  organizationName: string,
  board?: CRMVisualizationResponse
) {
  const people = useMemo(() => boardPeople(board), [board]);
  const [badges, setBadges] = useState<Map<string, BoardBadges>>(new Map());
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((n) => n + 1), []);
  const [open, setOpen] = useState<{ chip: Chip; badge: BoardBadges }>();
  const { polled } = useUnread();

  useEffect(() => {
    if (!people) {
      setBadges(new Map());
      return undefined;
    }
    let current = true;
    PeopleService.getBoardBadges({
      orgId,
      requestBody: people.split(",").map((key) => {
        const [kind, id] = key.split("-");
        return { kind: kind as PersonKind, id: Number(id) };
      }),
    })
      .then((list) => {
        if (current) {
          setBadges(
            new Map(list.map((badge) => [personKey(badge.row), badge]))
          );
        }
      })
      .catch(() => {
        if (current) {
          Notifications.error("The cards' counts could not load.");
        }
      });
    return () => {
      current = false;
    };
  }, [orgId, people, reloads, polled]);

  // The open window follows the new counts. Once its items are handled it
  // closes, except the task window, which shows the task done.
  useEffect(() => {
    setOpen((shown) => {
      if (!shown) return shown;
      const fresh = badges.get(personKey(shown.badge.row));
      if (fresh && fresh[shown.chip] > 0) return { ...shown, badge: fresh };
      return shown.chip === "tasks" ? shown : undefined;
    });
  }, [badges]);

  // Takes the chip off its card at once (its window closes) while the
  // dismiss is sent. Then the counts reload, nobody waiting: dismissing
  // onboarding can bring out a Needs attention item an open step hid, and a
  // failure brings the chip back.
  const dismiss = (chip: Chip, badge: BoardBadges) => {
    const { row } = badge;
    setBadges((current) =>
      new Map(current).set(personKey(row), { ...badge, [chip]: 0 })
    );
    (chip === "onboarding"
      ? PeopleService.dismissOnboarding({
          orgId,
          requestBody: { kind: row.kind, id: row.id },
        })
      : dismissIssues(orgId, row)
    )
      .catch(() => Notifications.error("Could not dismiss. Try again."))
      .finally(reload);
  };

  const card = (personType: Auth0AccountRole, id: number) => {
    const badge = badges.get(personKey({ kind: CARD_KINDS[personType], id }));
    const shown = CHIPS.filter(({ chip }) => badge && badge[chip] > 0);
    if (!badge || shown.length === 0) return null;
    return (
      <span className="board-chips" onMouseDown={stop} onTouchStart={stop}>
        {shown.map(({ chip, label, Icon, className }) => (
          <button
            key={chip}
            type="button"
            className={`board-chip ${className}`}
            onClick={(e) => {
              // The chip's window opens, not the card's.
              e.stopPropagation();
              setOpen({ chip, badge });
            }}
          >
            <Icon aria-hidden />
            {label}
            <span className="board-chip__count">{badge[chip]}</span>
          </button>
        ))}
      </span>
    );
  };

  const chipWindow = open && (
    <ChipWindow
      badge={open.badge}
      chip={open.chip}
      orgId={orgId}
      organizationName={organizationName}
      onClose={() => setOpen(undefined)}
      onChange={reload}
      onDismiss={() => dismiss(open.chip, open.badge)}
    />
  );

  return { card, chipWindow, reload };
}
